<template>
    <Teleport to="body">
      <Transition name="tutorial-fade">
        <div
          v-if="visible && targetRect"
          class="tutorial-root"
        >
          <!-- Блокируем всё, кроме выделенного пункта -->
          <div
            class="interaction-blocker blocker-top"
            :style="topBlockerStyle"
          />
  
          <div
            class="interaction-blocker blocker-left"
            :style="leftBlockerStyle"
          />
  
          <div
            class="interaction-blocker blocker-right"
            :style="rightBlockerStyle"
          />
  
          <div
            class="interaction-blocker blocker-bottom"
            :style="bottomBlockerStyle"
          />
  
          <!-- Spotlight -->
          <div
            class="spotlight"
            :style="spotlightStyle"
          ></div>
  
          <!-- Стрелка -->
          <svg
            v-if="arrowPath"
            class="arrow-layer"
            width="100%"
            height="100%"
            aria-hidden="true"
          >
            <defs>
              <linearGradient
                id="vacanciesTutorialGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  stop-color="#9B8CFF"
                />
  
                <stop
                  offset="100%"
                  stop-color="#4C9BFF"
                />
              </linearGradient>
  
              <marker
                id="vacanciesArrowHead"
                markerWidth="12"
                markerHeight="12"
                refX="9"
                refY="5"
                orient="auto"
              >
                <path
                  d="M0,0 L10,5 L0,10 Z"
                  fill="#5597FF"
                />
              </marker>
            </defs>
  
            <path
              :d="arrowPath"
              class="arrow-path"
              marker-end="url(#vacanciesArrowHead)"
            />
          </svg>
  
          <!-- Карточка -->
          <div
            ref="cardRef"
            class="tutorial-card"
            :style="cardStyle"
          >
            <div class="tutorial-top">
              <div class="tutorial-step">
                <span class="step-dot"></span>
                Ваши вакансии
              </div>
  
              <button
                type="button"
                class="close-button"
                aria-label="Закрыть"
                @click="closeTutorial"
              >
                ×
              </button>
            </div>
  
            <div class="icon-wrapper">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <rect
                  x="3"
                  y="5.5"
                  width="18"
                  height="15"
                  rx="3"
                  stroke="currentColor"
                  stroke-width="1.7"
                />
  
                <path
                  d="M8 5.5V4.2C8 3.54 8.54 3 9.2 3H14.8C15.46 3 16 3.54 16 4.2V5.5"
                  stroke="currentColor"
                  stroke-width="1.7"
                />
  
                <path
                  d="M7.5 11H16.5"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                />
  
                <path
                  d="M7.5 15H13.5"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                />
              </svg>
            </div>
  
            <h2 class="tutorial-title">
              Всё под контролем
            </h2>
  
            <p class="tutorial-description">
              В разделе
              <strong>«Мои вакансии»</strong>
              собраны все вакансии, которые вы создали.
            </p>
  
            <div class="features">
              <div class="feature">
                <div class="feature-icon">
                  ✦
                </div>
  
                <div>
                  <span class="feature-title">
                    Управляйте вакансиями
                  </span>
  
                  <span class="feature-text">
                    Редактируйте опубликованные вакансии и следите за их статусом.
                  </span>
                </div>
              </div>
  
              <div class="feature">
                <div class="feature-icon responses">
                  ↳
                </div>
  
                <div>
                  <span class="feature-title">
                    Следите за откликами
                  </span>
  
                  <span class="feature-text">
                    Новые кандидаты и отклики всегда будут рядом с нужной вакансией.
                  </span>
                </div>
              </div>
            </div>
  
            <div class="hint">
              <span class="hint-icon">💡</span>
  
              <span>
                Нажмите на выделенный пункт, чтобы открыть ваши вакансии.
              </span>
            </div>
  
            <div class="tutorial-actions">
              <button
                type="button"
                class="later-button"
                @click="closeTutorial"
              >
                Понятно
              </button>
  
              <button
                type="button"
                class="open-button"
                @click="openVacancies"
              >
                Открыть вакансии
  
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M9 6L15 12L9 18"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </template>
  
  <script setup>
  
  import {
    computed,
    nextTick,
    onBeforeUnmount,
    ref,
    watch,
  } from "vue";
  
  import UserService from "../api/UserService";

  const props = defineProps({
    visible: {
      type: Boolean,
      default: false,
    },
  
    targetElement: {
      type: Object,
      default: null,
    },
  });
  
  const emit = defineEmits([
    "close",
    "open",
  ]);
  
  const SPOTLIGHT_PADDING = 6;
  const CARD_GAP = 46;
  
  const targetRect = ref(null);
  const cardRef = ref(null);
  
  const cardPosition = ref({
    top: 0,
    left: 0,
  });

  const completeTutorial = async () => {
  try {
    console.log(
      "🎓 Сохраняем прохождение второго туториала"
    );

    await UserService.markSecondTutorialAsSeen();

    console.log(
      "✅ Второй туториал сохранён"
    );

  } catch (error) {
    console.error(
      "❌ Ошибка сохранения второго туториала:",
      error
    );
  }
};
  
  const arrowPath = ref("");
  
  let activeTarget = null;
  let frameId = null;
  
  const resolveTarget = () => {
    return props.targetElement || null;
  };
  
  const updatePosition = async () => {
    if (!props.visible) {
      return;
    }
  
    const element = resolveTarget();
  
    if (!element) {
      targetRect.value = null;
      return;
    }
  
    const rect =
      element.getBoundingClientRect();
  
    targetRect.value = {
      top:
        rect.top -
        SPOTLIGHT_PADDING,
  
      left:
        rect.left -
        SPOTLIGHT_PADDING,
  
      right:
        rect.right +
        SPOTLIGHT_PADDING,
  
      bottom:
        rect.bottom +
        SPOTLIGHT_PADDING,
  
      width:
        rect.width +
        SPOTLIGHT_PADDING * 2,
  
      height:
        rect.height +
        SPOTLIGHT_PADDING * 2,
    };
  
    await nextTick();
  
    calculateCardPosition();
  };
  
  const calculateCardPosition = async () => {
    if (
      !targetRect.value ||
      !cardRef.value
    ) {
      return;
    }
  
    await nextTick();
  
    const target =
      targetRect.value;
  
    const card =
      cardRef.value.getBoundingClientRect();
  
    const margin = 20;
  
    /*
     * Dropdown расположен справа,
     * поэтому карточку стараемся показать слева.
     */
    let left =
      target.left -
      card.width -
      CARD_GAP;
  
    let top =
      target.top +
      target.height / 2 -
      card.height / 2;
  
    left = Math.max(
      margin,
      Math.min(
        left,
        window.innerWidth -
          card.width -
          margin
      )
    );
  
    top = Math.max(
      margin,
      Math.min(
        top,
        window.innerHeight -
          card.height -
          margin
      )
    );
  
    cardPosition.value = {
      top,
      left,
    };
  
    await nextTick();
  
    calculateArrow();
  };
  
  const calculateArrow = () => {
    if (
      !targetRect.value ||
      !cardRef.value
    ) {
      return;
    }
  
    const target =
      targetRect.value;
  
    const card =
      cardRef.value.getBoundingClientRect();
  
    const startX =
      card.right - 4;
  
    const startY =
      card.top +
      card.height * 0.44;
  
    const endX =
      target.left +
      target.width / 2;
  
    const endY =
      target.top +
      target.height / 2;
  
    const distance =
      Math.abs(
        endX -
        startX
      );
  
    arrowPath.value = `
      M ${startX} ${startY}
  
      C
      ${startX + distance * 0.42}
      ${startY - 24},
  
      ${endX - distance * 0.3}
      ${endY + 18},
  
      ${endX}
      ${endY}
    `.replace(/\s+/g, " ");
  };
  
  const scheduleUpdate = () => {
    if (frameId) {
      cancelAnimationFrame(frameId);
    }
  
    frameId =
      requestAnimationFrame(
        updatePosition
      );
  };
  
  const spotlightStyle = computed(() => {
    if (!targetRect.value) {
      return {};
    }
  
    return {
      top:
        `${targetRect.value.top}px`,
  
      left:
        `${targetRect.value.left}px`,
  
      width:
        `${targetRect.value.width}px`,
  
      height:
        `${targetRect.value.height}px`,
    };
  });
  
  const cardStyle = computed(() => ({
    top:
      `${cardPosition.value.top}px`,
  
    left:
      `${cardPosition.value.left}px`,
  }));
  
  const topBlockerStyle = computed(() => ({
    height:
      `${Math.max(
        0,
        targetRect.value?.top ?? 0
      )}px`,
  }));
  
  const bottomBlockerStyle = computed(() => ({
    top:
      `${targetRect.value?.bottom ?? 0}px`,
  }));
  
  const leftBlockerStyle = computed(() => ({
    top:
      `${targetRect.value?.top ?? 0}px`,
  
    width:
      `${Math.max(
        0,
        targetRect.value?.left ?? 0
      )}px`,
  
    height:
      `${targetRect.value?.height ?? 0}px`,
  }));
  
  const rightBlockerStyle = computed(() => ({
    top:
      `${targetRect.value?.top ?? 0}px`,
  
    left:
      `${targetRect.value?.right ?? 0}px`,
  
    height:
      `${targetRect.value?.height ?? 0}px`,
  }));
  
  const closeTutorial = async () => {
  await completeTutorial();

  emit("close");
};
  
  const openVacancies = async () => {
    detachTargetListener();

    await completeTutorial();

    emit("open");
    emit("close");

    await nextTick();

    resolveTarget()?.click();
  };
  
  const handleTargetClick = async () => {
    await completeTutorial();

    emit("open");
    emit("close");
  };
  
  const attachTargetListener = () => {
    detachTargetListener();
  
    activeTarget =
      resolveTarget();
  
    activeTarget?.addEventListener(
      "click",
      handleTargetClick
    );
  };
  
  const detachTargetListener = () => {
    activeTarget?.removeEventListener(
      "click",
      handleTargetClick
    );
  
    activeTarget = null;
  };
  
  watch(
    [
      () => props.visible,
      () => props.targetElement,
    ],
    async ([visible]) => {
      if (!visible) {
        detachTargetListener();
  
        targetRect.value = null;
        arrowPath.value = "";
  
        return;
      }
  
      await nextTick();
  
      await updatePosition();
  
      attachTargetListener();
    },
    {
      flush: "post",
      immediate: true,
    }
  );
  
  window.addEventListener(
    "resize",
    scheduleUpdate,
    {
      passive: true,
    }
  );
  
  window.addEventListener(
    "scroll",
    scheduleUpdate,
    true
  );
  
  onBeforeUnmount(() => {
    detachTargetListener();
  
    window.removeEventListener(
      "resize",
      scheduleUpdate
    );
  
    window.removeEventListener(
      "scroll",
      scheduleUpdate,
      true
    );
  
    if (frameId) {
      cancelAnimationFrame(frameId);
    }
  });
  </script>
  
  <style scoped>
  .tutorial-root {
    position: fixed;
    inset: 0;
  
    z-index: 2000000;
  
    pointer-events: none;
  }
  
  .interaction-blocker {
    position: fixed;
  
    z-index: 1;
  
    pointer-events: auto;
  }
  
  .blocker-top {
    top: 0;
    left: 0;
    right: 0;
  }
  
  .blocker-bottom {
    left: 0;
    right: 0;
    bottom: 0;
  }
  
  .blocker-left {
    left: 0;
  }
  
  .blocker-right {
    right: 0;
  }
  
  /* Spotlight */
  
  .spotlight {
    position: fixed;
  
    z-index: 2;
  
    border:
      2px solid
      rgba(122, 154, 255, 0.95);
  
    border-radius: 12px;
  
    pointer-events: none;
  
    box-shadow:
      0 0 0 9999px
        rgba(5, 9, 18, 0.76),
  
      0 0 0 5px
        rgba(89, 137, 255, 0.13),
  
      0 0 32px
        rgba(80, 135, 255, 0.68);
  
    animation:
      vacancySpotlight
      2s
      ease-in-out
      infinite;
  }
  
  @keyframes vacancySpotlight {
    50% {
      box-shadow:
        0 0 0 9999px
          rgba(5, 9, 18, 0.76),
  
        0 0 0 8px
          rgba(89, 137, 255, 0.08),
  
        0 0 46px
          rgba(80, 135, 255, 0.85);
    }
  }
  
  /* Arrow */
  
  .arrow-layer {
    position: fixed;
    inset: 0;
  
    z-index: 3;
  
    overflow: visible;
  
    pointer-events: none;
  }
  
  .arrow-path {
    fill: none;
  
    stroke:
      url(#vacanciesTutorialGradient);
  
    stroke-width: 3;
  
    stroke-linecap: round;
  
    stroke-dasharray: 8 7;
  
    filter:
      drop-shadow(
        0 4px 9px
        rgba(80, 135, 255, 0.4)
      );
  
    animation:
      vacancyArrowFlow
      1.3s
      linear
      infinite;
  }
  
  @keyframes vacancyArrowFlow {
    to {
      stroke-dashoffset: -30;
    }
  }
  
  /* Card */
  
  .tutorial-card {
    position: fixed;
  
    z-index: 4;
  
    width:
      min(
        385px,
        calc(100vw - 32px)
      );
  
    padding: 25px;
  
    box-sizing: border-box;
  
    color: white;
  
    background:
      linear-gradient(
        145deg,
        rgba(29, 34, 48, 0.99),
        rgba(17, 21, 31, 0.99)
      );
  
    border:
      1px solid
      rgba(255, 255, 255, 0.11);
  
    border-radius: 25px;
  
    box-shadow:
      0 35px 100px
        rgba(0, 0, 0, 0.5),
  
      0 12px 35px
        rgba(0, 0, 0, 0.28),
  
      inset 0 1px 0
        rgba(255, 255, 255, 0.06);
  
    pointer-events: auto;
  
    animation:
      vacancyTutorialAppear
      0.42s
      cubic-bezier(
        0.22,
        1,
        0.36,
        1
      );
  }
  
  .tutorial-card::before {
    content: "";
  
    position: absolute;
  
    top: -150px;
    right: -100px;
  
    width: 230px;
    height: 230px;
  
    background:
      radial-gradient(
        circle,
        rgba(122, 100, 255, 0.19),
        transparent 70%
      );
  
    pointer-events: none;
  }
  
  @keyframes vacancyTutorialAppear {
    from {
      opacity: 0;
  
      transform:
        translateY(10px)
        scale(0.96);
    }
  
    to {
      opacity: 1;
  
      transform:
        translateY(0)
        scale(1);
    }
  }
  
  /* Top */
  
  .tutorial-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  
    margin-bottom: 18px;
  }
  
  .tutorial-step {
    display: inline-flex;
    align-items: center;
  
    gap: 7px;
  
    padding: 6px 10px;
  
    color: #a9c7ff;
  
    background:
      rgba(76, 142, 255, 0.09);
  
    border:
      1px solid
      rgba(76, 142, 255, 0.18);
  
    border-radius: 999px;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 10px;
  
    text-transform: uppercase;
  
    letter-spacing: 0.5px;
  }
  
  .step-dot {
    width: 6px;
    height: 6px;
  
    border-radius: 50%;
  
    background: #699eff;
  
    box-shadow:
      0 0 10px
      rgba(105, 158, 255, 0.75);
  }
  
  .close-button {
    width: 30px;
    height: 30px;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    padding: 0;
  
    border: none;
    border-radius: 50%;
  
    color:
      rgba(255, 255, 255, 0.45);
  
    background:
      rgba(255, 255, 255, 0.05);
  
    font-size: 20px;
  
    cursor: pointer;
  
    transition:
      background 0.2s ease,
      color 0.2s ease;
  }
  
  .close-button:hover {
    color: white;
  
    background:
      rgba(255, 255, 255, 0.1);
  }
  
  /* Icon */
  
  .icon-wrapper {
    width: 52px;
    height: 52px;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    margin-bottom: 17px;
  
    color: #8bb6ff;
  
    background:
      linear-gradient(
        145deg,
        rgba(87, 140, 255, 0.18),
        rgba(115, 88, 255, 0.08)
      );
  
    border:
      1px solid
      rgba(105, 150, 255, 0.18);
  
    border-radius: 16px;
  }
  
  .icon-wrapper svg {
    width: 27px;
    height: 27px;
  }
  
  /* Text */
  
  .tutorial-title {
    margin:
      0 0
      9px;
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size: 23px;
  
    letter-spacing:
      -0.4px;
  }
  
  .tutorial-description {
    margin:
      0 0
      19px;
  
    color:
      rgba(255, 255, 255, 0.6);
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 12px;
  
    line-height: 1.6;
  }
  
  .tutorial-description strong {
    color:
      rgba(255, 255, 255, 0.96);
  }
  
  /* Features */
  
  .features {
    display: flex;
    flex-direction: column;
  
    gap: 9px;
  
    margin-bottom: 15px;
  }
  
  .feature {
    display: flex;
    align-items: flex-start;
  
    gap: 11px;
  
    padding:
      12px
      13px;
  
    background:
      rgba(255, 255, 255, 0.035);
  
    border:
      1px solid
      rgba(255, 255, 255, 0.065);
  
    border-radius: 14px;
  }
  
  .feature-icon {
    width: 31px;
    height: 31px;
  
    flex: 0 0 31px;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    border-radius: 10px;
  
    color: #a995ff;
  
    background:
      rgba(145, 108, 255, 0.11);
  }
  
  .feature-icon.responses {
    color: #76b7ff;
  
    background:
      rgba(76, 151, 255, 0.1);
  }
  
  .feature-title,
  .feature-text {
    display: block;
  }
  
  .feature-title {
    margin-bottom: 3px;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 11px;
  }
  
  .feature-text {
    color:
      rgba(255, 255, 255, 0.42);
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 9px;
  
    line-height: 1.5;
  }
  
  /* Hint */
  
  .hint {
    display: flex;
    align-items: center;
  
    gap: 8px;
  
    margin-bottom: 20px;
  
    padding:
      10px
      12px;
  
    color:
      rgba(255, 255, 255, 0.45);
  
    background:
      rgba(77, 142, 255, 0.055);
  
    border-radius: 12px;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 9px;
  
    line-height: 1.45;
  }
  
  .hint-icon {
    font-size: 13px;
  }
  
  /* Actions */
  
  .tutorial-actions {
    display: grid;
  
    grid-template-columns:
      auto
      1fr;
  
    gap: 9px;
  }
  
  .later-button,
  .open-button {
    min-height: 45px;
  
    border: none;
    border-radius: 14px;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 11px;
  
    cursor: pointer;
  
    transition:
      transform 0.18s ease,
      background 0.18s ease,
      box-shadow 0.18s ease;
  }
  
  .later-button {
    padding:
      0 18px;
  
    color:
      rgba(255, 255, 255, 0.65);
  
    background:
      rgba(255, 255, 255, 0.065);
  
    border:
      1px solid
      rgba(255, 255, 255, 0.075);
  }
  
  .open-button {
    display: flex;
    align-items: center;
    justify-content: center;
  
    gap: 8px;
  
    padding:
      0 18px;
  
    color: white;
  
    background:
      linear-gradient(
        135deg,
        #397df5,
        #6553e8
      );
  
    box-shadow:
      0 12px 28px
        rgba(64, 116, 244, 0.29);
  }
  
  .open-button svg {
    width: 15px;
    height: 15px;
  
    transition:
      transform 0.18s ease;
  }
  
  .open-button:hover {
    transform:
      translateY(-1px);
  
    box-shadow:
      0 16px 34px
        rgba(64, 116, 244, 0.38);
  }
  
  .open-button:hover svg {
    transform:
      translateX(3px);
  }
  
  /* Light */
  
  [data-theme="light"] .tutorial-card {
    color: #182033;
  
    background:
      linear-gradient(
        145deg,
        rgba(255, 255, 255, 0.99),
        rgba(246, 248, 253, 0.99)
      );
  
    border:
      1px solid
      rgba(24, 32, 51, 0.1);
  
    box-shadow:
      0 35px 100px
        rgba(34, 49, 76, 0.2),
  
      0 12px 35px
        rgba(34, 49, 76, 0.08);
  }
  
  [data-theme="light"]
  .tutorial-description {
    color: #717b8e;
  }
  
  [data-theme="light"]
  .tutorial-description strong {
    color: #202a3d;
  }
  
  [data-theme="light"]
  .feature {
    background:
      rgba(24, 40, 70, 0.035);
  
    border-color:
      rgba(24, 40, 70, 0.07);
  }
  
  [data-theme="light"]
  .feature-text {
    color: #7b8494;
  }
  
  [data-theme="light"]
  .hint {
    color: #758197;
  }
  
  [data-theme="light"]
  .later-button {
    color: #536074;
  
    background:
      rgba(25, 43, 72, 0.055);
  
    border-color:
      rgba(25, 43, 72, 0.07);
  }
  
  [data-theme="light"]
  .close-button {
    color: #738096;
  
    background:
      rgba(25, 43, 72, 0.05);
  }
  
  /* Mobile */
  
  @media (max-width: 768px) {
    .tutorial-card {
      width:
        calc(100vw - 30px);
  
      padding: 21px;
  
      border-radius: 22px;
    }
  
    .tutorial-title {
      font-size: 20px;
    }
  
    .tutorial-actions {
      grid-template-columns: 1fr;
    }
  
    .open-button {
      order: -1;
    }
  }
  </style>